import { DialogComponent } from '@theme/dialog';
import { ThemeEvents } from '@theme/events';

/**
 * Notify Stock Modal Component
 *
 * Displays a modal form for customers to sign up for back-in-stock notifications.
 * Syncs form data with the Shopify Forms app embed for backend integration.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLDivElement} notifyForm - The notification form container.
 * @property {HTMLDivElement} formFields - The form fields container (hidden on success).
 * @property {HTMLInputElement} skuInput - SKU input field.
 * @property {HTMLInputElement} emailInput - Email input field.
 * @property {HTMLInputElement} firstNameInput - First name input field.
 * @property {HTMLInputElement} lastNameInput - Last name input field.
 * @property {HTMLInputElement} companyInput - Company name input field.
 * @property {HTMLButtonElement} submitButton - Form submit button.
 * @property {HTMLElement} submitText - Submit button text.
 * @property {HTMLElement} submitLoading - Submit button loading state.
 * @property {HTMLElement} errorMessage - Error message container.
 * @property {HTMLElement} errorText - Error message text.
 * @property {HTMLElement} successMessage - Success message container (shown on success).
 *
 * @extends DialogComponent
 */
export class NotifyStockModal extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });

    this.hidden = this.dataset.variantAvailable === 'true';

    setTimeout(() => this.#syncToShopifyForms(), 500);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * Shows the dialog and resets form state.
   */
  showDialog() {
    super.showDialog();

    this.#hideMessages();
    this.#setLoadingState(false);

    const { skuInput } = this.refs;
    const skuValue = this.#getCurrentSku();
    if (skuInput) {
      /** @type {HTMLInputElement} */ (skuInput).value = skuValue;
    }

    this.#syncToShopifyForms();
  }

  /**
   * Handles the notify button click - validates and submits to Shopify Forms.
   *
   * @param {Event} event - The click event.
   */
  handleSubmit(event) {
    event.preventDefault();
    event.stopPropagation();

    const { emailInput } = this.refs;
    const email = /** @type {HTMLInputElement} */ (emailInput)?.value;

    if (!email || !this.#isValidEmail(email)) {
      this.#showError('Please enter a valid email address.');
      /** @type {HTMLInputElement} */ (emailInput)?.focus();
      return;
    }

    this.#setLoadingState(true);
    this.#hideMessages();
    this.#submitToShopifyForms();
  }

  /**
   * Gets the Shopify Forms ID from the data attribute.
   *
   * @returns {string} The form ID.
   */
  get shopifyFormId() {
    return this.dataset.shopifyFormId || '';
  }

  /**
   * Gets the current SKU value with fallback to product title.
   *
   * @returns {string} The SKU or product title.
   */
  #getCurrentSku() {
    const { skuInput } = this.refs;
    return /** @type {HTMLInputElement} */ (skuInput)?.value || this.dataset.variantSku || this.dataset.productTitle || '';
  }

  /**
   * Handles variant update events to sync SKU and visibility.
   *
   * @param {CustomEvent} event - The variant update event.
   */
  #onVariantUpdate = (event) => {
    const { detail } = event;

    if (detail?.data?.productId && this.dataset.productId && detail.data.productId !== this.dataset.productId) {
      return;
    }

    const variant = detail?.resource || detail?.data?.variant;
    const sku = variant?.sku || this.dataset.productTitle || '';

    const { skuInput } = this.refs;
    if (skuInput) {
      /** @type {HTMLInputElement} */ (skuInput).value = sku;
    }
    this.dataset.variantSku = sku;

    const shopifySkuInput = this.#getShopifyFormsInput('custom#product_sku');
    if (shopifySkuInput) {
      this.#setInputValue(shopifySkuInput, sku);
    }

    this.hidden = variant?.available ?? true;
  };

  /**
   * Gets the shadow root of the Shopify Forms embed.
   *
   * @returns {ShadowRoot | null} The shadow root or null.
   */
  #getShopifyFormsShadowRoot() {
    const formId = this.shopifyFormId;
    const container = document.querySelector(`[data-forms-id="${formId}"]`);
    const embed = container?.querySelector('shopify-forms-embed') ?? document.querySelector('shopify-forms-embed');
    return embed?.shadowRoot ?? null;
  }

  /**
   * Gets an input from the Shopify Forms shadow DOM.
   *
   * @param {string} fieldName - The field name (e.g., 'email', 'custom#product_sku').
   * @returns {HTMLInputElement | null} The input element or null.
   */
  #getShopifyFormsInput(fieldName) {
    const shadowRoot = this.#getShopifyFormsShadowRoot();
    if (!shadowRoot) return null;

    const escapedId = fieldName.replace('#', '\\#');
    return /** @type {HTMLInputElement | null} */ (
      shadowRoot.querySelector(`#${escapedId}, [data-testid="field-${fieldName}"]`)
    );
  }

  /**
   * Syncs all form values to the hidden Shopify Forms embed.
   */
  #syncToShopifyForms() {
    const { emailInput, firstNameInput, lastNameInput, companyInput } = this.refs;

    const fields = {
      email: /** @type {HTMLInputElement} */ (emailInput)?.value || this.dataset.customerEmail || '',
      first_name: /** @type {HTMLInputElement} */ (firstNameInput)?.value || this.dataset.customerFirstName || '',
      last_name: /** @type {HTMLInputElement} */ (lastNameInput)?.value || this.dataset.customerLastName || '',
      'custom#company_name': /** @type {HTMLInputElement} */ (companyInput)?.value || this.dataset.customerCompany || '',
      'custom#product_sku': this.#getCurrentSku(),
    };

    for (const [name, value] of Object.entries(fields)) {
      const input = this.#getShopifyFormsInput(name);
      if (input && value) {
        this.#setInputValue(input, value);
      }
    }
  }

  /**
   * Submits form data to Shopify Forms.
   */
  async #submitToShopifyForms() {
    const formId = this.shopifyFormId;
    const container = document.querySelector(`[data-forms-id="${formId}"]`);

    if (!this.#getShopifyFormsShadowRoot()) {
      console.warn('Shopify Forms embed not found');
      this.#showError();
      this.#setLoadingState(false);
      return;
    }

    try {
      container?.classList.add('notify-stock-submitting');

      this.#syncToShopifyForms();

      await new Promise((resolve) => setTimeout(resolve, 200));

      const shadowRoot = this.#getShopifyFormsShadowRoot();
      const submitBtn = shadowRoot?.querySelector('[data-testid="btn-form-submit"], button[type="submit"]');

      if (submitBtn instanceof HTMLButtonElement) {
        submitBtn.click();

        const success = await this.#waitForShopifyFormsResponse();

        if (success) {
          this.#showSuccess();
          this.#resetForm();
        } else {
          this.#showError();
        }
      } else {
        console.warn('Shopify Forms submit button not found');
        this.#showError();
      }
    } catch (error) {
      console.error('Form submission error:', error);
      this.#showError();
    } finally {
      container?.classList.remove('notify-stock-submitting');
      this.#setLoadingState(false);
    }
  }

  /**
   * Waits for the Shopify Forms response.
   *
   * @returns {Promise<boolean>} Whether the submission was successful.
   */
  async #waitForShopifyFormsResponse() {
    const shadowRoot = this.#getShopifyFormsShadowRoot();
    if (!shadowRoot) return true;

    return new Promise((resolve) => {
      let attempts = 0;
      const maxAttempts = 20;

      const checkForResponse = () => {
        attempts++;

        const successMessage = shadowRoot.querySelector('[data-testid="success-message"], [class*="success"]');
        if (successMessage?.textContent?.trim()) {
          resolve(true);
          return;
        }

        const errorMessage = shadowRoot.querySelector('[data-testid="error-message"], [class*="error"]');
        if (errorMessage?.textContent?.trim() && !errorMessage.hasAttribute('hidden')) {
          resolve(false);
          return;
        }

        if (attempts >= maxAttempts) {
          resolve(true);
          return;
        }

        setTimeout(checkForResponse, 200);
      };

      setTimeout(checkForResponse, 300);
    });
  }

  /**
   * Sets an input value with React compatibility (triggers state updates).
   *
   * @param {HTMLInputElement} input - The input element.
   * @param {string} value - The value to set.
   */
  #setInputValue(input, value) {
    const nativeInputValueSetter = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value')?.set;

    if (nativeInputValueSetter) {
      nativeInputValueSetter.call(input, value);
    } else {
      input.value = value;
    }

    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /**
   * Validates email format.
   *
   * @param {string} email - The email to validate.
   * @returns {boolean} Whether the email is valid.
   */
  #isValidEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  /**
   * Sets the loading state of the form.
   *
   * @param {boolean} loading - Whether the form is loading.
   */
  #setLoadingState(loading) {
    const { submitButton, submitText, submitLoading } = this.refs;

    if (submitButton) {
      /** @type {HTMLButtonElement} */ (submitButton).disabled = loading;
    }
    if (submitText) {
      /** @type {HTMLElement} */ (submitText).hidden = loading;
    }
    if (submitLoading) {
      /** @type {HTMLElement} */ (submitLoading).hidden = !loading;
    }
  }

  /**
   * Shows an error message.
   *
   * @param {string} [message] - Custom error message.
   */
  #showError(message) {
    const { errorMessage, errorText } = this.refs;

    if (errorMessage) {
      /** @type {HTMLElement} */ (errorMessage).hidden = false;
    }
    if (errorText && message) {
      /** @type {HTMLElement} */ (errorText).textContent = message;
    }
  }

  /**
   * Shows the success message and hides form fields.
   */
  #showSuccess() {
    const { formFields, successMessage } = this.refs;

    if (formFields) {
      /** @type {HTMLElement} */ (formFields).hidden = true;
    }
    if (successMessage) {
      /** @type {HTMLElement} */ (successMessage).hidden = false;
    }
  }

  /**
   * Hides all messages and shows form fields (reset state).
   */
  #hideMessages() {
    const { formFields, errorMessage, successMessage } = this.refs;

    if (formFields) {
      /** @type {HTMLElement} */ (formFields).hidden = false;
    }
    if (errorMessage) {
      /** @type {HTMLElement} */ (errorMessage).hidden = true;
    }
    if (successMessage) {
      /** @type {HTMLElement} */ (successMessage).hidden = true;
    }
  }

  /**
   * Resets the form fields - only clears non-readonly (unlocked) fields.
   * Locked fields (pre-filled customer data, SKU) are preserved.
   */
  #resetForm() {
    const { emailInput, firstNameInput, lastNameInput, companyInput, skuInput } = this.refs;

    const inputs = [emailInput, firstNameInput, lastNameInput, companyInput];

    for (const input of inputs) {
      if (input && !/** @type {HTMLInputElement} */ (input).readOnly) {
        /** @type {HTMLInputElement} */ (input).value = '';
      }
    }

    if (skuInput) {
      /** @type {HTMLInputElement} */ (skuInput).value = this.#getCurrentSku();
    }
  }
}

if (!customElements.get('notify-stock-modal')) {
  customElements.define('notify-stock-modal', NotifyStockModal);
}
